import React from 'react';
import { Stack, Chip, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

// status -> MUI chip color. 'pending' (with admin) and 'flagged' (AI unsure,
// needs human review) are submitted states — not the red of missing/rejected.
const STATUS_COLOR = { approved: 'success', pending: 'info', flagged: 'warning', rejected: 'error', missing: 'error' };
// compact label per requirement key
const SHORT = {
  government_id: 'ID',
  company_registration: 'Reg',
  director_id: 'Dir ID',
  tax_clearance: 'Tax',
  eac_practising_certificate: 'Cert',
  agency_engagement_letter: 'Letter',
  eac_agency: 'EAC',
  // Property-listing dossier items
  title_deed: 'Deed',
  local_authority: 'Rates',
  survey_beacons: 'Survey',
  utilities: 'Utilities',
  environmental: 'EMA',
  legal: 'Conveyancer',
  valuation: 'Valuation',
  approved_building_plans: 'Plans',
  occupancy_or_compliance_cert: 'Occupancy',
};

// `noun` names what is being counted — documents for applicant KYC, items for a listing dossier.
export default function DocumentCompletenessStrip({ summary, noun = 'Documents' }) {
  if (!summary || !summary.requiredCount) return null;
  const { requiredCount, satisfiedCount, complete, items } = summary;
  // Fall back gracefully if an older payload lacks the submitted fields.
  const submittedCount = summary.submittedCount ?? satisfiedCount;
  const allSubmitted = summary.allSubmitted ?? false;
  // Three states: all approved (complete) → everything uploaded but a review is
  // still outstanding (awaiting review) → something the applicant must upload.
  let headColor = 'warning';
  let headLabel = `Incomplete · ${noun} ${submittedCount}/${requiredCount}`;
  if (complete) {
    headColor = 'success';
    headLabel = `Complete · ${noun} ${satisfiedCount}/${requiredCount}`;
  } else if (allSubmitted) {
    headColor = 'info';
    headLabel = `Awaiting review · ${noun} ${submittedCount}/${requiredCount}`;
  }
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
      <Chip
        size="small"
        icon={complete ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
        color={headColor}
        variant={complete ? 'filled' : 'outlined'}
        label={headLabel}
      />
      {items.map((it) => (
        <Tooltip key={it.key} title={`${it.label}: ${it.status}`}>
          <Chip
            size="small"
            aria-label={`${it.label}: ${it.status}`}
            label={SHORT[it.key] || it.label}
            color={STATUS_COLOR[it.status] || 'default'}
            variant={it.status === 'approved' ? 'filled' : 'outlined'}
          />
        </Tooltip>
      ))}
    </Stack>
  );
}
